import React, { useState } from 'react'
import { ChevronLeft, Share2 } from 'lucide-react'

interface SocialItem {
  label: string
  href: string
  external: boolean
  icon: React.ReactNode
}

const SOCIAL_ITEMS: SocialItem[] = [
  {
    label: 'Instagram — Merritt & Shawn',
    href: 'https://www.instagram.com/merrittandshawn/',
    external: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn — Shawn McDonnell',
    href: 'https://www.linkedin.com/in/shawn-mcdonnell-41587513b',
    external: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8.09h4.56V23H.22V8.09zM8.34 8.09h4.37v2.04h.06c.61-1.16 2.1-2.38 4.32-2.38 4.62 0 5.47 3.04 5.47 7v8.25h-4.55v-7.31c0-1.74-.03-3.99-2.43-3.99-2.44 0-2.81 1.9-2.81 3.86V23H8.34V8.09z" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn — Merritt Cooch',
    href: 'https://www.linkedin.com/in/merritt-cooch-2496bb125',
    external: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8.09h4.56V23H.22V8.09zM8.34 8.09h4.37v2.04h.06c.61-1.16 2.1-2.38 4.32-2.38 4.62 0 5.47 3.04 5.47 7v8.25h-4.55v-7.31c0-1.74-.03-3.99-2.43-3.99-2.44 0-2.81 1.9-2.81 3.86V23H8.34V8.09z" />
      </svg>
    ),
  },
  // TODO-CLIENT: add Zillow Premier Agent profiles when client supplies exact URLs (brief C.3)
  {
    label: 'Email Shawn & Merritt',
    href: 'mailto:shawn@jacklingo.com',
    external: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5" aria-hidden="true">
        <rect x="2.5" y="5" width="19" height="14" rx="2" />
        <path d="M3 7l9 6 9-6" />
      </svg>
    ),
  },
]

const SocialRail: React.FC = () => {
  // Wide screens have enough gutter for the rail; compact layouts use menu social links.
  const [open, setOpen] = useState(true)

  // The wide-screen rail remains vertically centered and clear of the content column.
  const collapsedPos =
    'left-2 top-24 bottom-auto lg:top-1/2 lg:-translate-y-1/2'
  const expandedPos =
    'left-1 top-24 bottom-auto lg:left-2 lg:top-1/2 lg:-translate-y-1/2'

  // Collapsed: compact share control that can restore the full rail.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Show social links"
        className={`fixed z-40 hidden h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-primary-ink/75 text-white shadow-lg backdrop-blur-md transition-colors duration-300 hover:text-accent-gold min-[1440px]:flex lg:h-11 lg:w-11 ${collapsedPos}`}
      >
        <Share2 className="w-5 h-5" aria-hidden="true" />
      </button>
    )
  }

  return (
    <nav
      aria-label="Social links"
      className={`fixed z-40 hidden flex-col items-center gap-0.5 rounded-full border border-white/15 bg-primary-ink/75 px-1 py-1.5 shadow-lg backdrop-blur-md min-[1440px]:flex lg:gap-1 lg:py-2 ${expandedPos}`}
    >
      <span className="block w-px h-3 bg-white/25" aria-hidden="true" />
      {SOCIAL_ITEMS.map((item) => (
        <a
          key={item.label}
          href={item.href}
          aria-label={item.label}
          {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="flex h-7 w-7 items-center justify-center rounded-full text-white transition-colors duration-300 hover:bg-white/10 hover:text-accent-gold lg:h-8 lg:w-8"
        >
          {item.icon}
        </a>
      ))}
      <span className="block w-px h-3 bg-white/25" aria-hidden="true" />
      {/* Collapse control — tuck the rail away while browsing */}
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Hide social links"
        className="flex h-7 w-7 items-center justify-center rounded-full text-white/60 transition-colors duration-300 hover:bg-white/10 hover:text-accent-gold lg:h-8 lg:w-8"
      >
        <ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </nav>
  )
}

export default SocialRail
